import React, { useState, useMemo, useCallback } from 'react';
import { Modal } from '../common/Modal/Modal';
import { VoorzieningItem } from '../../types';
import { relationshipsAPI, analysisAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import styles from './GebiedsanalyseModule.module.css';

// Category to role/type mapping
const CATEGORY_ROLE_MAP: Record<string, { role: string; orgType: string; category: string }> = {
  scholen: { role: 'stakeholder', orgType: 'educational', category: 'stakeholder' },
  zorginstellingen: { role: 'stakeholder', orgType: 'healthcare', category: 'stakeholder' },
  sportverenigingen: { role: 'stakeholder', orgType: 'community', category: 'stakeholder' },
  religieuze_instellingen: { role: 'stakeholder', orgType: 'community', category: 'stakeholder' },
  culturele_organisaties: { role: 'stakeholder', orgType: 'community', category: 'stakeholder' },
  detailhandel: { role: 'stakeholder', orgType: 'business', category: 'business' },
  horeca: { role: 'stakeholder', orgType: 'business', category: 'business' },
  industrie: { role: 'stakeholder', orgType: 'business', category: 'business' },
  ambacht: { role: 'stakeholder', orgType: 'business', category: 'business' },
  energie: { role: 'stakeholder', orgType: 'business', category: 'business' },
  logistiek: { role: 'stakeholder', orgType: 'business', category: 'business' },
  zorg: { role: 'stakeholder', orgType: 'healthcare', category: 'stakeholder' },
  dienstverlening: { role: 'stakeholder', orgType: 'business', category: 'business' },
  agrarisch: { role: 'stakeholder', orgType: 'business', category: 'business' },
};

const CATEGORY_LABELS: Record<string, string> = {
  scholen: 'School/Onderwijs',
  zorginstellingen: 'Zorginstelling',
  sportverenigingen: 'Sportvereniging',
  religieuze_instellingen: 'Religieuze instelling',
  culturele_organisaties: 'Culturele organisatie',
  detailhandel: 'Detailhandel',
  horeca: 'Horeca',
  industrie: 'Industrie',
  ambacht: 'Ambacht',
  energie: 'Energie',
  logistiek: 'Logistiek',
  zorg: 'Zorg',
  dienstverlening: 'Dienstverlening',
  agrarisch: 'Agrarisch',
};

interface KvkData {
  kvkNummer: string;
  naam: string;
  type?: string;
  actief?: boolean;
  matchScore: number;
  websites: string[];
  sbiCodes: string[];
  sbiDescriptions: string[];
  employees: number | null;
  address: {
    street?: string;
    houseNumber?: string;
    postcode?: string;
    city?: string;
  };
  vestigingsnummer?: string | null;
}

interface EnrichedItem extends VoorzieningItem {
  source: 'organisatie' | 'bedrijf';
  kvk?: KvkData | null;
}

interface ImportToRelatiebeheerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  organisaties: VoorzieningItem[];
  bedrijven: VoorzieningItem[];
}

type SourceType = 'all' | 'organisaties' | 'bedrijven';

export const ImportToRelatiebeheer: React.FC<ImportToRelatiebeheerProps> = ({
  isOpen,
  onClose,
  projectId,
  organisaties,
  bedrijven,
}) => {
  const toast = useToast();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [sourceFilter, setSourceFilter] = useState<SourceType>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // KvK enrichment state
  const [kvkEnabled, setKvkEnabled] = useState(false);
  const [kvkLoading, setKvkLoading] = useState(false);
  const [kvkData, setKvkData] = useState<Map<string, KvkData | null>>(new Map());
  const [kvkEnriched, setKvkEnriched] = useState(false);

  // Combine and deduplicate items
  const allItems = useMemo(() => {
    const orgItems = organisaties.map(item => ({ ...item, source: 'organisatie' as const }));
    const bizItems = bedrijven.map(item => ({ ...item, source: 'bedrijf' as const }));
    // Filter out items without a real name (e.g. unnamed "Industrieel gebouw" from OSM)
    return [...orgItems, ...bizItems].filter(item =>
      item.name && item.name.trim() !== '' && item.hasName !== false
    );
  }, [organisaties, bedrijven]);

  // Filter items
  const filteredItems = useMemo(() => {
    let items = allItems;

    if (sourceFilter === 'organisaties') {
      items = items.filter(i => i.source === 'organisatie');
    } else if (sourceFilter === 'bedrijven') {
      items = items.filter(i => i.source === 'bedrijf');
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      items = items.filter(i =>
        i.name.toLowerCase().includes(q) ||
        i.category.toLowerCase().includes(q) ||
        i.subcategory.toLowerCase().includes(q)
      );
    }

    return items;
  }, [allItems, sourceFilter, searchQuery]);

  const toggleItem = (osmId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(osmId)) {
        next.delete(osmId);
      } else {
        next.add(osmId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filteredItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredItems.map(i => i.osmId)));
    }
  };

  // KvK verrijking uitvoeren
  const handleKvkEnrich = useCallback(async () => {
    if (kvkEnriched || allItems.length === 0) return;

    setKvkLoading(true);
    try {
      const itemsToEnrich = allItems.map(item => ({
        name: item.name,
        lat: item.lat,
        lon: item.lon,
        category: item.category,
        osmId: item.osmId,
      }));

      const enrichedItems = await analysisAPI.kvkEnrich(itemsToEnrich);

      const newKvkData = new Map<string, KvkData | null>();
      for (const item of enrichedItems) {
        if (item.osmId) {
          newKvkData.set(item.osmId, item.kvk || null);
        }
      }

      setKvkData(newKvkData);
      setKvkEnriched(true);

      const matchCount = enrichedItems.filter((i: any) => i.kvk !== null).length;
      if (matchCount > 0) {
        toast.success(`${matchCount} items verrijkt met KvK-gegevens`);
      } else {
        toast.info('Geen KvK-matches gevonden', 'Er zijn geen bedrijven gevonden die overeenkomen met de geselecteerde items');
      }
    } catch (error: any) {
      console.error('KvK verrijking mislukt:', error);
      toast.error('KvK verrijking mislukt', 'Kon geen verbinding maken met de KvK API');
    } finally {
      setKvkLoading(false);
    }
  }, [allItems, kvkEnriched, toast]);

  // Toggle KvK verrijking
  const handleKvkToggle = useCallback(() => {
    const newEnabled = !kvkEnabled;
    setKvkEnabled(newEnabled);

    if (newEnabled && !kvkEnriched) {
      handleKvkEnrich();
    }
  }, [kvkEnabled, kvkEnriched, handleKvkEnrich]);

  const handleImport = async () => {
    if (selectedIds.size === 0) return;

    setImporting(true);
    try {
      const items = allItems
        .filter(item => selectedIds.has(item.osmId))
        .map(item => {
          const mapping = CATEGORY_ROLE_MAP[item.category] || {
            role: 'stakeholder',
            orgType: 'other',
            category: 'stakeholder',
          };

          // Extract contact info from OSM tags if available
          const osmTags = item.tags || {};
          const contact: Record<string, any> = {};
          const social: Record<string, any> = {};

          const phone = osmTags['phone'] || osmTags['contact:phone'] || '';
          const email = osmTags['email'] || osmTags['contact:email'] || '';
          let website = osmTags['website'] || osmTags['contact:website'] || osmTags['url'] || '';

          if (phone) contact.phone = phone;
          if (email) contact.email = email;
          if (website) social.website = website;

          // KvK-verrijkte gegevens toevoegen
          const itemKvk = kvkEnabled ? kvkData.get(item.osmId) : null;
          const tags = [
            CATEGORY_LABELS[item.category] || item.category,
            item.source === 'organisatie' ? 'Organisatie' : 'Bedrijf',
            'Geimporteerd uit gebiedsanalyse',
          ];
          let notes = `Geimporteerd uit gebiedsanalyse. Categorie: ${CATEGORY_LABELS[item.category] || item.category}${item.subcategory ? ` (${item.subcategory})` : ''}. Afstand: ${item.distance} km.`;

          if (itemKvk) {
            // Website uit KvK overschrijft OSM als die leeg is
            if (itemKvk.websites && itemKvk.websites.length > 0 && !social.website) {
              social.website = itemKvk.websites[0];
            }

            // KvK-nummer als tag
            tags.push(`KvK: ${itemKvk.kvkNummer}`);

            // SBI-omschrijving toevoegen aan notities
            if (itemKvk.sbiDescriptions && itemKvk.sbiDescriptions.length > 0) {
              notes += ` SBI: ${itemKvk.sbiDescriptions.join(', ')}.`;
            }

            // Werknemers toevoegen aan notities
            if (itemKvk.employees) {
              notes += ` Werknemers: ${itemKvk.employees}.`;
            }

            // Geverifieerd adres uit KvK
            if (itemKvk.address) {
              const addr = itemKvk.address;
              if (addr.street) contact.street = addr.street;
              if (addr.houseNumber) contact.houseNumber = addr.houseNumber;
              if (addr.postcode) contact.postalCode = addr.postcode;
              if (addr.city) contact.city = addr.city;
            }
          }

          return {
            type: 'organization' as const,
            name: item.name,
            lat: item.lat,
            lon: item.lon,
            organization: {
              name: item.name,
              type: mapping.orgType,
            },
            contact: Object.keys(contact).length > 0 ? contact : undefined,
            social: Object.keys(social).length > 0 ? social : undefined,
            role: mapping.role,
            category: mapping.category,
            influence: { level: 'medium' },
            interest: { level: 'medium' },
            tags,
            notes,
          };
        });

      // Import in batches van 20 om "request entity too large" te voorkomen
      const BATCH_SIZE = 20;
      let totalCreated = 0;
      let totalSkipped = 0;

      for (let i = 0; i < items.length; i += BATCH_SIZE) {
        const batch = items.slice(i, i + BATCH_SIZE);
        const result = await relationshipsAPI.bulkImport(projectId, batch);
        totalCreated += result.created;
        totalSkipped += result.skipped;
      }

      if (totalCreated > 0) {
        toast.success(
          `${totalCreated} contact${totalCreated !== 1 ? 'en' : ''} geimporteerd`,
          totalSkipped > 0 ? `${totalSkipped} overgeslagen (bestonden al)` : undefined
        );
      } else if (totalSkipped > 0) {
        toast.info('Geen nieuwe contacten', 'Alle geselecteerde items bestonden al in relatiebeheer');
      }

      setSelectedIds(new Set());
      onClose();
    } catch (error: any) {
      const message = error.response?.data?.error || 'Kon contacten niet importeren';
      toast.error('Import mislukt', message);
    } finally {
      setImporting(false);
    }
  };

  const orgCount = allItems.filter(i => i.source === 'organisatie').length;
  const bizCount = allItems.filter(i => i.source === 'bedrijf').length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Importeer naar Relatiebeheer"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, maxHeight: '70vh' }}>
        {/* Description */}
        <p style={{ fontSize: 13, color: '#666', margin: 0, lineHeight: 1.5 }}>
          Selecteer organisaties en bedrijven uit de gebiedsanalyse om als contacten toe te voegen aan relatiebeheer.
          Rol en categorie worden automatisch ingevuld op basis van het type.
        </p>

        {/* Filters + KvK toggle */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 4 }}>
            {[
              { key: 'all' as SourceType, label: `Alles (${allItems.length})` },
              { key: 'organisaties' as SourceType, label: `Organisaties (${orgCount})` },
              { key: 'bedrijven' as SourceType, label: `Bedrijven (${bizCount})` },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setSourceFilter(tab.key)}
                style={{
                  padding: '4px 10px',
                  fontSize: 11,
                  fontWeight: sourceFilter === tab.key ? 600 : 400,
                  border: `1px solid ${sourceFilter === tab.key ? '#E6007E' : '#ddd'}`,
                  borderRadius: 16,
                  background: sourceFilter === tab.key ? 'rgba(230, 0, 126, 0.08)' : 'white',
                  color: sourceFilter === tab.key ? '#E6007E' : '#666',
                  cursor: 'pointer',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* KvK verrijking toggle */}
          <button
            onClick={handleKvkToggle}
            disabled={kvkLoading}
            style={{
              padding: '4px 10px',
              fontSize: 11,
              fontWeight: kvkEnabled ? 600 : 400,
              border: `1px solid ${kvkEnabled ? '#1d4ed8' : '#ddd'}`,
              borderRadius: 16,
              background: kvkEnabled ? 'rgba(29, 78, 216, 0.08)' : 'white',
              color: kvkEnabled ? '#1d4ed8' : '#666',
              cursor: kvkLoading ? 'wait' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              opacity: kvkLoading ? 0.7 : 1,
            }}
          >
            {kvkLoading ? (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
              </svg>
            ) : (
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
            )}
            {kvkLoading ? 'Verrijken...' : 'Verrijk met KvK'}
          </button>

          <input
            type="text"
            placeholder="Zoek op naam of categorie..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              flex: 1,
              minWidth: 160,
              padding: '5px 10px',
              fontSize: 12,
              border: '1px solid #ddd',
              borderRadius: 6,
              outline: 'none',
            }}
          />
        </div>

        {/* KvK info tekst */}
        {kvkEnabled && (
          <p style={{ fontSize: 11, color: '#1d4ed8', margin: 0, padding: '2px 0', lineHeight: 1.4 }}>
            Zoekt aanvullende bedrijfsgegevens via de Kamer van Koophandel
            {kvkEnriched && (
              <span style={{ color: '#666', marginLeft: 6 }}>
                — {Array.from(kvkData.values()).filter(v => v !== null).length} van {allItems.length} gematcht
              </span>
            )}
          </p>
        )}

        {/* Select all toggle */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '6px 0',
          borderBottom: '1px solid #eee',
        }}>
          <button
            onClick={toggleAll}
            style={{
              fontSize: 12,
              color: '#E6007E',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 600,
              padding: 0,
            }}
          >
            {selectedIds.size === filteredItems.length && filteredItems.length > 0
              ? 'Deselecteer alles'
              : `Selecteer alles (${filteredItems.length})`}
          </button>
          <span style={{ fontSize: 11, color: '#999' }}>
            {selectedIds.size} geselecteerd
          </span>
        </div>

        {/* Items list */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          maxHeight: '40vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}>
          {filteredItems.length === 0 ? (
            <p style={{ fontSize: 12, color: '#999', textAlign: 'center', padding: 20 }}>
              Geen items gevonden
            </p>
          ) : (
            filteredItems.map(item => {
              const isSelected = selectedIds.has(item.osmId);
              const mapping = CATEGORY_ROLE_MAP[item.category];
              const roleLabel = mapping?.role === 'decision_maker' ? 'Beslisser'
                : mapping?.role === 'stakeholder' ? 'Stakeholder'
                : 'Stakeholder';

              const itemKvk = kvkEnabled ? kvkData.get(item.osmId) : null;

              return (
                <button
                  key={item.osmId}
                  onClick={() => toggleItem(item.osmId)}
                  className={styles.omgevingsListItem}
                  style={{
                    background: isSelected ? 'rgba(230, 0, 126, 0.06)' : 'white',
                    borderLeft: isSelected ? '3px solid #E6007E' : '3px solid transparent',
                  }}
                >
                  {/* Checkbox */}
                  <span style={{
                    width: 16,
                    height: 16,
                    borderRadius: 3,
                    border: isSelected ? '2px solid #E6007E' : '2px solid #ccc',
                    background: isSelected ? '#E6007E' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {isSelected && (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </span>

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontSize: 12, fontWeight: 500, color: '#323232' }}>
                      {item.name}
                    </span>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <span style={{
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 10,
                        border: `1px solid ${item.source === 'organisatie' ? '#10b981' : '#f59e0b'}`,
                        color: item.source === 'organisatie' ? '#10b981' : '#f59e0b',
                      }}>
                        {item.source === 'organisatie' ? 'Organisatie' : 'Bedrijf'}
                      </span>
                      <span style={{ fontSize: 10, color: '#999' }}>
                        {CATEGORY_LABELS[item.category] || item.category}
                      </span>
                      {item.subcategory && (
                        <span style={{ fontSize: 10, color: '#bbb' }}>
                          {item.subcategory}
                        </span>
                      )}
                      <span style={{ fontSize: 10, color: '#bbb' }}>
                        {item.distance} km
                      </span>
                      <span style={{
                        fontSize: 10,
                        padding: '1px 6px',
                        borderRadius: 10,
                        background: '#f5f5f5',
                        color: '#666',
                      }}>
                        {roleLabel}
                      </span>
                    </div>

                    {/* KvK verrijkte gegevens */}
                    {itemKvk && (
                      <div style={{
                        display: 'flex',
                        gap: 6,
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        marginTop: 2,
                      }}>
                        <span style={{
                          fontSize: 10,
                          padding: '1px 6px',
                          borderRadius: 10,
                          background: 'rgba(29, 78, 216, 0.08)',
                          border: '1px solid rgba(29, 78, 216, 0.2)',
                          color: '#1d4ed8',
                        }}>
                          KvK {itemKvk.kvkNummer}
                        </span>
                        {itemKvk.websites && itemKvk.websites.length > 0 && (
                          <span style={{ fontSize: 10, color: '#1d4ed8' }}>
                            {itemKvk.websites[0].replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
                          </span>
                        )}
                        {itemKvk.sbiDescriptions && itemKvk.sbiDescriptions.length > 0 && (
                          <span style={{ fontSize: 10, color: '#666' }}>
                            {itemKvk.sbiDescriptions[0]}
                          </span>
                        )}
                        {itemKvk.employees && (
                          <span style={{ fontSize: 10, color: '#999' }}>
                            {itemKvk.employees} werknemers
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </button>
              );
            })
          )}
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 8,
          paddingTop: 12,
          borderTop: '1px solid #eee',
        }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              background: 'transparent',
              border: '1px solid #ddd',
              borderRadius: 6,
              color: '#666',
              cursor: 'pointer',
            }}
          >
            Annuleren
          </button>
          <button
            onClick={handleImport}
            disabled={selectedIds.size === 0 || importing}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              background: selectedIds.size === 0 || importing ? '#ccc' : '#E6007E',
              border: 'none',
              borderRadius: 6,
              color: 'white',
              fontWeight: 600,
              cursor: selectedIds.size === 0 || importing ? 'not-allowed' : 'pointer',
            }}
          >
            {importing
              ? 'Importeren...'
              : `Importeer ${selectedIds.size} contact${selectedIds.size !== 1 ? 'en' : ''}`}
          </button>
        </div>
      </div>

      {/* Keyframe animation for spinner */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Modal>
  );
};
